// src/views/Tarjetas/AsignarEESS.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import { hasScope } from "../../../utils/auth";

const PAGE_SIZE = 200;
const SIZE_CLIENTES = 100;
const SIZE_TARJETAS = 500; // traemos todas las del cliente en una
const TARJ_TIPO = "CLIENTE"; // coincide con el enum del backend
const ESTADOS_TARJETA_ASIGNABLES = [27, 29]; // 27=En renovación, 29=Activa

function useDebounced(v, delay = 250) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

const isAbort = (e) => e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

// ===== helpers paginados (EESS / Productos) =====
async function fetchAllOptions(buildUrl, mapFn, { signal } = {}) {
  let page = 0;
  let hasMore = true;
  const acc = [];
  while (hasMore) {
    const url = buildUrl(page);
    const data = await apiFetchJson(url, { signal });
    const items = data?.items ?? data?.content ?? [];
    acc.push(...items.map(mapFn));
    hasMore = Boolean(data?.hasMore ?? items.length >= (data?.size ?? PAGE_SIZE));
    page = (data?.page ?? page) + 1;
  }
  // dedup por id
  const seen = new Set();
  return acc.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

async function fetchAllIds(buildUrl, { signal } = {}) {
  let page = 0;
  let hasMore = true;
  const ids = [];
  while (hasMore) {
    const url = buildUrl(page);
    const data = await apiFetchJson(url, { signal });
    const items = data?.items ?? data?.content ?? [];
    ids.push(...items.map((x) => x.id));
    hasMore = Boolean(data?.hasMore ?? items.length >= (data?.size ?? PAGE_SIZE));
    page = (data?.page ?? page) + 1;
  }
  return Array.from(new Set(ids));
}

export default function AsignarEESS({ onSaved = () => {}, onConsult = () => {} }) {
  
  
  const canAsignarMasivoEess = hasScope("actualización masiva:eess_a_todas_las_tarjetas");
  
  const canAsignarEessTarjetas = hasScope("actualización masiva:asignar_eess_a_tarjetas");
  
  const canAsignarProductosTarjetas = hasScope("actualización masiva:asignar_productos_a_tarjetas");

  // ====== estado común ======
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ====== Overlay / Popup ======
  const [overlay, setOverlay] = useState({ open: false, mode: "hidden", message: "" });
  useEffect(() => {
    const onKey = (e) => {
      if (!overlay.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [overlay.open]);

  // ====== Cliente ======
  const [cliOpen, setCliOpen] = useState(false);
  const [cliInput, setCliInput] = useState("");
  const debCli = useDebounced(cliInput, 250);
  const [cliItems, setCliItems] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const cliAbort = useRef(null);
  const cliBlur = useRef(null);
  const cliBoxRef = useRef(null);
  const [cliPage, setCliPage] = useState(0);
  const [cliHasMore, setCliHasMore] = useState(false);
  const [cliLoading, setCliLoading] = useState(false);
  const cliSeq = useRef(0);

  // ====== Tarjeta ======
  // para saber si seteamos cliente automáticamente desde una tarjeta elegida
  const clienteDerivadoRef = useRef(false);
  const clienteDerivadoNombreRef = useRef("");

  const [tarOpen, setTarOpen] = useState(false);
  const [tarInput, setTarInput] = useState("");
  const debTar = useDebounced(tarInput, 250); // lo dejamos por si en el futuro queremos usarlo
  const [tarItems, setTarItems] = useState([]);
  const [tarjetaSel, setTarjetaSel] = useState(null);
  const tarBlur = useRef(null);
  const tarBoxRef = useRef(null);
  const tarInputRef = useRef(null);

  const [tarLoading, setTarLoading] = useState(false);
  const [tarPage, setTarPage] = useState(0);
  const [tarHasMore, setTarHasMore] = useState(false);
  const tarAbortRef = useRef(null);
  const tarSeqRef = useRef(0);

  // ====== EE.SS. / Productos ======
  const [activeTab, setActiveTab] = useState(() => {
    if (canAsignarProductosTarjetas) return "productos";
    if (canAsignarEessTarjetas) return "eess";
    if (canAsignarMasivoEess) return "todas";
    return null;
  });
  


  const [eess, setEess] = useState([]);
  const [openEess, setOpenEess] = useState(false);
  const [searchEess, setSearchEess] = useState("");
  const [selectedEessIds, setSelectedEessIds] = useState([]);
  const eessRef = useRef(null);

  const [productos, setProductos] = useState([]);
  const [openProd, setOpenProd] = useState(false);
  const [searchProd, setSearchProd] = useState("");
  const [selectedProdIds, setSelectedProdIds] = useState([]);
  const prodRef = useRef(null);

  // "EE.SS. a todas las tarjetas"
  const [confirmarTodas, setConfirmarTodas] = useState(false);

  // == control de asignaciones (abort/seq) ==
  const asignSeq = useRef(0);
  const asignAbortEess = useRef(null);
  const asignAbortProd = useRef(null);

  const cancelAsignadas = () => {
    try {
      asignAbortEess.current?.abort();
    } catch {}
    try {
      asignAbortProd.current?.abort();
    } catch {}
    asignAbortEess.current = null;
    asignAbortProd.current = null;
  };

  // ====== cambio de pestaña ======
  useEffect(() => {
    setSearchEess("");
    setSearchProd("");
    setError("");
    // limpiar Cliente / Tarjeta y asignaciones
    setClienteSel(null);
    setCliInput("");
    setTarjetaSel(null);
    setTarInput("");
    setTarItems([]);
    setTarPage(0);
    setTarHasMore(false);
    cancelAsignadas();

    setSelectedEessIds([]);
    setSelectedProdIds([]);
  }, [activeTab]);

  // ====== Helpers visuales ======
  const filteredEess = useMemo(() => {
    const q = searchEess.trim().toLowerCase();
    if (!q) return eess;
    return eess.filter((o) =>
      (String(o.id) + " " + (o.label ?? "")).toLowerCase().includes(q),
    );
  }, [eess, searchEess]);

  const filteredProd = useMemo(() => {
    const q = searchProd.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter((o) =>
      (String(o.id) + " " + (o.label ?? "")).toLowerCase().includes(q),
    );
  }, [productos, searchProd]);

  const labelEess =
    selectedEessIds.length === 0
      ? "Seleccionar"
      : selectedEessIds.length === 1
      ? "1 seleccionada"
      : `${selectedEessIds.length} seleccionadas`;

  const labelProd =
    selectedProdIds.length === 0
      ? "Seleccionar"
      : selectedProdIds.length === 1
      ? "1 seleccionado"
      : `${selectedProdIds.length} seleccionados`;

  const toggleEess = (id) => {
    setSelectedEessIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    if (activeTab === "eess" || activeTab === "todas") {
      setError("");
    }
  };

  const marcarTodasEess = () => {
    setSelectedEessIds(filteredEess.map((f) => f.id));
    if (activeTab === "eess" || activeTab === "todas") {
      setError("");
    }
  };

  const desmarcarTodasEess = () =>
    setSelectedEessIds((p) => p.filter((id) => !filteredEess.some((f) => f.id === id)));

  const toggleProd = (id) =>
    setSelectedProdIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const marcarTodasProd = () =>
    setSelectedProdIds((p) => Array.from(new Set([...p, ...filteredProd.map((f) => f.id)])));

  const desmarcarTodasProd = () =>
    setSelectedProdIds((p) => p.filter((id) => !filteredProd.some((f) => f.id === id)));

  // ====== cerrar dropdowns click afuera ======
  useEffect(() => {
    const onDoc = (e) => {
      if (cliBoxRef.current && !cliBoxRef.current.contains(e.target)) setCliOpen(false);
      if (tarBoxRef.current && !tarBoxRef.current.contains(e.target)) setTarOpen(false);
      if (eessRef.current && !eessRef.current.contains(e.target)) setOpenEess(false);
      if (prodRef.current && !prodRef.current.contains(e.target)) setOpenProd(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ====== precarga (lista completa) EESS y Productos ======
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctrl = new AbortController();

        const [es, pr] = await Promise.all([
          fetchAllOptions(
            (page) =>
              apiRoutes.eessHabilitadas.autocompleteUrl("eess", "", page, PAGE_SIZE, true),
            (r) => ({ id: r.id, label: r.label }),
            { signal: ctrl.signal },
          ),
          fetchAllOptions(
            (page) =>
              apiRoutes.productoTarjeta.autocompleteUrl("producto", "", page, PAGE_SIZE, true),
            (r) => ({ id: r.id, label: r.label }),
            { signal: ctrl.signal },
          ),
        ]);

        if (!cancelled) {
          setEess(es);
          setProductos(pr);
        }
      } catch (e) {
        if (!cancelled && !isAbort(e)) {
          console.error("Precarga EESS/Productos:", e);
          setEess([]);
          setProductos([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ====== Clientes: browse + search ======
  const cancelCli = () => {
    try {
      cliAbort.current?.abort();
    } catch {}
    cliAbort.current = null;
  };

  const fetchClientesSearch = async ({ q, page }) => {
    cancelCli();
    const ctrl = new AbortController();
    cliAbort.current = ctrl;
    const seq = ++cliSeq.current;

    if (!page || page === 0) {
      setCliItems([]);
      setCliHasMore(false);
    }
    setCliLoading(true);
    try {
      const url = `${apiRoutes.clientes.autocomplete}?q=${encodeURIComponent(
        q ?? "",
      )}&page=${encodeURIComponent(page ?? 0)}&size=${SIZE_CLIENTES}&tipo=cliente`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (seq !== cliSeq.current) return;

      const mapped = (data?.items ?? []).map((c) => ({
        id: c.id,
        etiqueta: c.nombre ?? c.label ?? c.razonSocial ?? "",
      }));
      setCliItems((prev) => (page && page > 0 ? [...prev, ...mapped] : mapped));
      setCliHasMore(Boolean(data?.hasMore));
      setCliPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("Autocomplete clientes:", e);
    } finally {
      setCliLoading(false);
    }
  };

  const fetchClientesBrowse = async ({ page }) => {
    cancelCli();
    const ctrl = new AbortController();
    cliAbort.current = ctrl;
    const seq = ++cliSeq.current;

    if (!page || page === 0) {
      setCliItems([]);
      setCliHasMore(false);
    }
    setCliLoading(true);
    try {
      const url = `${apiRoutes.clientes.list}?page=${encodeURIComponent(
        page ?? 0,
      )}&size=${SIZE_CLIENTES}`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (seq !== cliSeq.current) return;

      const mapped = (data?.items ?? []).map((c) => ({
        id: c.id,
        etiqueta: c.nombre ?? "",
      }));
      setCliItems((prev) => (page && page > 0 ? [...prev, ...mapped] : mapped));
      const hasMore = (data?.items ?? []).length >= (data?.size ?? SIZE_CLIENTES);
      setCliHasMore(hasMore);
      setCliPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("List clientes:", e);
    } finally {
      setCliLoading(false);
    }
  };

  useEffect(() => {
    if (!cliOpen) return;
    const q = (debCli ?? "").trim();
    if (q) fetchClientesSearch({ q, page: 0 });
    else fetchClientesBrowse({ page: 0 });
  }, [cliOpen, debCli]);

  const onClientListScroll = (e) => {
    const el = e.currentTarget;
    if (!cliHasMore || cliLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      const q = (debCli ?? "").trim();
      if (q) fetchClientesSearch({ q, page: (cliPage ?? 0) + 1 });
      else fetchClientesBrowse({ page: (cliPage ?? 0) + 1 });
    }
  };

  // ====== Tarjetas helpers nuevos ======
  const cancelTar = () => {
    try {
      tarAbortRef.current?.abort();
    } catch {}
    tarAbortRef.current = null;
  };

  const mapTarjetaDto = (t) => {
    const numero = t.numero ?? t.tarjetaNumero ?? t.label ?? "";
    return {
      id: t.id,
      numero,
      etiqueta: numero,
      clienteId: t.cliente?.id ?? t.clienteId ?? null,
      clienteNombre: t.cliente?.nombre ?? t.clienteNombre ?? t.razonSocial ?? "",
    };
  };

  const loadTarjetasBrowseGlobal = async (page = 0) => {
    cancelTar();
    const ctrl = new AbortController();
    tarAbortRef.current = ctrl;
    const mySeq = ++tarSeqRef.current;

    if (page === 0) {
      setTarItems([]);
      setTarPage(0);
      setTarHasMore(false);
    }
    setTarLoading(true);

    try {
      const url =
        apiRoutes.tarjetas.autocompleteUrl("numero", "", page, SIZE_TARJETAS, ESTADOS_TARJETA_ASIGNABLES) +
        `&codigoTipo=${TARJ_TIPO}`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (mySeq !== tarSeqRef.current) return;

      const mapped = (data?.items ?? []).map(mapTarjetaDto);
      setTarItems((prev) => (page === 0 ? mapped : [...prev, ...mapped]));
      setTarHasMore(Boolean(data?.hasMore));
      setTarPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("Browse tarjetas global:", e);
      if (page === 0) {
        setTarItems([]);
        setTarHasMore(false);
      }
    } finally {
      if (mySeq === tarSeqRef.current) {
        setTarLoading(false);
        tarAbortRef.current = null;
      }
    }
  };

  const loadTarjetasSearchGlobal = async (q, page = 0) => {
    cancelTar();
    const ctrl = new AbortController();
    tarAbortRef.current = ctrl;
    const mySeq = ++tarSeqRef.current;

    if (page === 0) {
      setTarItems([]);
      setTarPage(0);
      setTarHasMore(false);
    }
    setTarLoading(true);

    try {
      const url =
        apiRoutes.tarjetas.autocompleteUrl("numero", q ?? "", page, SIZE_TARJETAS, ESTADOS_TARJETA_ASIGNABLES) +
        `&codigoTipo=${TARJ_TIPO}`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (mySeq !== tarSeqRef.current) return;

      const mapped = (data?.items ?? []).map(mapTarjetaDto);
      setTarItems((prev) => (page === 0 ? mapped : [...prev, ...mapped]));
      setTarHasMore(Boolean(data?.hasMore));
      setTarPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("Search tarjetas global:", e);
      if (page === 0) setTarItems([]);
    } finally {
      if (mySeq === tarSeqRef.current) {
        setTarLoading(false);
        tarAbortRef.current = null;
      }
    }
  };

  const loadTarjetasBrowseCliente = async (clienteId, page = 0) => {
    if (!clienteId) return;
    cancelTar();
    const ctrl = new AbortController();
    tarAbortRef.current = ctrl;
    const mySeq = ++tarSeqRef.current;

    if (page === 0) {
      setTarItems([]);
      setTarPage(0);
      setTarHasMore(false);
    }
    setTarLoading(true);

    try {
      const url =
        apiRoutes.tarjetas.autocompleteUrl("numero", "", page, SIZE_TARJETAS, ESTADOS_TARJETA_ASIGNABLES) +
        `&clienteId=${encodeURIComponent(clienteId)}&codigoTipo=${TARJ_TIPO}`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (mySeq !== tarSeqRef.current) return;

      const mapped = (data?.items ?? []).map(mapTarjetaDto);

      setTarItems((prev) => {
        if (page === 0) {
          const head = mapped.length > 0 ? [{ id: "TODAS", numero: "", etiqueta: "Todas" }] : [];
          return [...head, ...mapped];
        }
        return [...prev, ...mapped];
      });

      setTarHasMore(Boolean(data?.hasMore));
      setTarPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("Browse tarjetas cliente:", e);
      if (page === 0) {
        setTarItems([]);
        setTarHasMore(false);
      }
    } finally {
      if (mySeq === tarSeqRef.current) {
        setTarLoading(false);
        tarAbortRef.current = null;
      }
    }
  };

  const loadTarjetasSearchCliente = async (clienteId, q, page = 0) => {
    if (!clienteId) return;
    cancelTar();
    const ctrl = new AbortController();
    tarAbortRef.current = ctrl;
    const mySeq = ++tarSeqRef.current;

    if (page === 0) {
      setTarItems([]);
      setTarPage(0);
      setTarHasMore(false);
    }
    setTarLoading(true);

    try {
      const url =
        apiRoutes.tarjetas.autocompleteUrl("numero", q ?? "", page, SIZE_TARJETAS, ESTADOS_TARJETA_ASIGNABLES) +
        `&clienteId=${encodeURIComponent(clienteId)}&codigoTipo=${TARJ_TIPO}`;
      const data = await apiFetchJson(url, { signal: ctrl.signal });
      if (mySeq !== tarSeqRef.current) return;

      const mapped = (data?.items ?? []).map(mapTarjetaDto);

      setTarItems((prev) => {
        if (page === 0) {
          const head = mapped.length > 0 ? [{ id: "TODAS", numero: "", etiqueta: "Todas" }] : [];
          return [...head, ...mapped];
        }
        return [...prev, ...mapped];
      });

      setTarHasMore(Boolean(data?.hasMore));
      setTarPage(data?.page ?? page ?? 0);
    } catch (e) {
      if (!isAbort(e)) console.error("Search tarjetas cliente:", e);
      if (page === 0) setTarItems([]);
    } finally {
      if (mySeq === tarSeqRef.current) {
        setTarLoading(false);
        tarAbortRef.current = null;
      }
    }
  };

  // ====== Scroll infinito tarjetas ======
  const onTarListScroll = (e) => {
    const el = e.currentTarget;
    if (tarLoading || !tarHasMore) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 8) return;

    const next = (tarPage ?? 0) + 1;
    const q = (tarInput ?? "").trim();

    if (clienteSel?.id) {
      if (q) loadTarjetasSearchCliente(clienteSel.id, q, next);
      else loadTarjetasBrowseCliente(clienteSel.id, next);
    } else {
      if (q) loadTarjetasSearchGlobal(q, next);
      else loadTarjetasBrowseGlobal(next);
    }
  };

  // ====== Selección de cliente ======
  const selectCliente = (opt) => {
    setClienteSel(opt);
    setCliInput(opt.etiqueta || "");
    setCliOpen(false);

    // limpiar selección de tarjeta / asignaciones
    setTarjetaSel(null);
    setTarInput("");
    setSelectedEessIds([]);
    setSelectedProdIds([]);
    setTarItems([]);
    setTarPage(0);
    setTarHasMore(false);
    cancelTar();
    cancelAsignadas();

    if (opt?.id != null) {
      // primer browse de tarjetas del cliente
      loadTarjetasBrowseCliente(opt.id);
      setTimeout(() => {
        tarInputRef.current?.focus();
        setTarOpen(true);
      }, 0);
    }
  };

  // ====== Selección de tarjeta ======
  const selectTarjeta = async (opt) => {
    setTarjetaSel(opt);
    setTarInput(opt.etiqueta || opt.numero || "");
    setTarOpen(false);

    // Si no hay cliente seleccionado, obtener el cliente real desde backend
    if (!clienteSel?.id && opt?.id && opt.id !== "TODAS") {
      try {
        const det = await apiFetchJson(apiRoutes.tarjetas.detail(opt.id));
        if (det?.clienteId) {
          const nuevoCliente = {
            id: det.clienteId,
            etiqueta: det.clienteNombre || "",
          };
          setClienteSel(nuevoCliente);
          setCliInput(det.clienteNombre || "");

          clienteDerivadoRef.current = true;
          clienteDerivadoNombreRef.current = det.clienteNombre || "";

          // a partir de ahora, comportamiento como "cliente seleccionado":
          loadTarjetasBrowseCliente(det.clienteId);
        }
      } catch (err) {
        console.error("Error obteniendo cliente de la tarjeta:", err);
      }
    }

    // limpiar tildados actuales antes de consultar livianos
    setSelectedEessIds([]);
    setSelectedProdIds([]);

    // Si es "TODAS", no cargamos livianos por tarjeta
    if (opt?.id === "TODAS") {
      cancelAsignadas();
      return;
    }

    // cancelar cargas anteriores (si el usuario cambia rápido de tarjeta)
    cancelAsignadas();
    const mySeq = ++asignSeq.current;
    const tarjetaId = opt.id;

    // 1) EE.SS. solo en pestaña "eess"
    if (activeTab === "eess") {
      try {
        const ctrlE = new AbortController();
        asignAbortEess.current = ctrlE;

        const eessIds = await fetchAllIds(
          (page) => apiRoutes.eessHabilitadas.asignadas(tarjetaId, page, PAGE_SIZE),
          { signal: ctrlE.signal },
        );

        if (mySeq === asignSeq.current) setSelectedEessIds(eessIds);
      } catch (e) {
        if (!isAbort(e)) console.error("EESS asignadas (liviano):", e);
      } finally {
        asignAbortEess.current = null;
      }
    }

    // 2) Productos solo en pestaña "productos"
    if (activeTab === "productos") {
      try {
        const ctrlP = new AbortController();
        asignAbortProd.current = ctrlP;

        const prodIds = await fetchAllIds(
          (page) => apiRoutes.productoTarjeta.asignados(tarjetaId, page, PAGE_SIZE),
          { signal: ctrlP.signal },
        );

        if (mySeq === asignSeq.current) setSelectedProdIds(prodIds);
      } catch (e) {
        if (!isAbort(e)) console.error("Productos asignados (liviano):", e);
      } finally {
        asignAbortProd.current = null;
      }
    }
  };

  const handleBlurTarjeta = () => {
    tarBlur.current = setTimeout(() => setTarOpen(false), 120);
  };

  // ====== Guardar ======
  const handleGuardar = async () => {
    setError("");
   
    if (activeTab === "productos") {
      if (!tarjetaSel?.id) {
        setError("Debes seleccionar una tarjeta.");
        return;
      }
      if (selectedProdIds.length === 0) {
        setError("Seleccioná al menos un producto.");
        return;
      }
    } else if (activeTab === "eess") {
      if (!tarjetaSel?.id) {
        setError("Debes seleccionar una tarjeta.");
        return;
      }
      if (selectedEessIds.length === 0) {
        setError("Seleccioná al menos una EE.S.");
        return;
      }
    } else if (activeTab === "todas") {
      if (selectedEessIds.length === 0) {
        setError("Seleccioná una EE.S.");
        return;
      }
    }

    setOverlay({ open: true, mode: "loading", message: "Generando solicitud…" });
    setLoading(true);

    try {
      let deleted = 0,
        inserted = 0;

      const TARJETAS_BASE = (apiRoutes?.tarjetas?.list || "").replace(
        /\/list(?:\?.*)?$/,
        "",
      );

      const tarjetaIdPayload =
        activeTab === "todas"
          ? "todas"
          : tarjetaSel?.id === "TODAS"
          ? "todas"
          : tarjetaSel?.id;

      const clienteIdPayload = activeTab === "todas" ? "todos" : clienteSel?.id ?? null;


      // EE.SS. solo en pestañas eess / todas
      if (activeTab === "eess" || activeTab === "todas") {
        const urlEst = `${TARJETAS_BASE}/asignar/estacion`;
        const r1 = await apiFetchJson(urlEst, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clienteId: clienteIdPayload,
            tarjetaId: tarjetaIdPayload,
            ids: selectedEessIds,
          }),
        });
        deleted += r1?.deletedCount ?? 0;
        inserted += r1?.insertedCount ?? 0;
      }

      // Productos solo en pestaña productos
      if (activeTab === "productos") {
        const urlProd = `${TARJETAS_BASE}/asignar/productos`;
        const r2 = await apiFetchJson(urlProd, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clienteId: clienteIdPayload,
            tarjetaId: tarjetaIdPayload,
            ids: selectedProdIds,
          }),
        });
        deleted += r2?.deletedCount ?? 0;
        inserted += r2?.insertedCount ?? 0;
      }

      const scopeMsg =
        activeTab === "todas"
          ? "todas las tarjetas"
          : tarjetaSel?.id === "TODAS"
          ? "todas las tarjetas del cliente"
          : `tarjeta ${tarjetaSel?.numero || ""}`;

      const successMsg = `Relaciones actualizadas (del: ${deleted}, ins: ${inserted}) para ${scopeMsg}.`;
     
      setOverlay({ open: true, mode: "success", message: successMsg });
    } catch (e) {
      const errMsg = e?.message || "No fue posible guardar la asignación.";
      setError(errMsg);
      setOverlay({ open: true, mode: "error", message: errMsg });
    } finally {
      setLoading(false);
    }
  };

  // ====== UI ======
  return (
    <div
      className="page"
      onMouseDown={() => {
        clearTimeout(cliBlur.current);
        clearTimeout(tarBlur.current);
      }}
    >
      <h1 className="page-title">⛽ Asignar EE.SS. y Productos a las Tarjetas</h1>
      <div className="border rounded-lg bg-white overflow-visible mb-8">
        <div className="px-6 py-6 space-y-4">
          {/* === PESTAÑAS === */}
          <div className="border-b mb-4 flex gap-6 px-2">
            
            {canAsignarProductosTarjetas && (
              <button
                type="button"
                className={`pb-2 ${
                  activeTab === "productos"
                    ? "border-b-2 border-green-600 text-green-700 font-semibold"
                    : "text-gray-600"
                }`}
                onClick={() => setActiveTab("productos")}
              >
                Productos
              </button>
            )}

            {canAsignarEessTarjetas && (
              <button
                type="button"
                className={`pb-2 ${
                  activeTab === "eess"
                    ? "border-b-2 border-green-600 text-green-700 font-semibold"
                    : "text-gray-600"
                }`}
                onClick={() => setActiveTab("eess")}
              >
                EE.SS.
              </button>
            )}

            {canAsignarMasivoEess && (
              <button
                type="button"
                className={`pb-2 ${
                  activeTab === "todas"
                    ? "border-b-2 border-green-600 text-green-700 font-semibold"
                    : "text-gray-600"
                }`}
                onClick={() => setActiveTab("todas")}
              >
                EE.SS. a Todas las tarjetas
              </button>
            )}

          </div>

          {/* ==== Pestañas: Productos / EE.SS. comparten Cliente + Tarjeta ==== */}
          {(activeTab === "productos" || activeTab === "eess") && (
            <>
              {/* Cliente */}
              <div className="grid grid-cols-[140px_1fr] items-start gap-3 max-w-3xl">
                <label className="label-strong text-right mt-2">Cliente:</label>
                <div ref={cliBoxRef} className="relative w-full">
                  <input
                    type="text"
                    placeholder="Nombre o razón social"
                    className="input w-full"
                    value={cliInput}
                    onChange={(e) => {
                      const v = e.target.value || "";
                      setCliInput(v);
                      setCliOpen(true);

                      if (clienteSel?.id || clienteDerivadoRef.current) {
                        setClienteSel(null);
                        clienteDerivadoRef.current = false;
                        clienteDerivadoNombreRef.current = "";

                        setTarjetaSel(null);
                        setTarInput("");
                        setSelectedEessIds([]);
                        setSelectedProdIds([]);
                        setTarItems([]);
                        setTarPage(0);
                        setTarHasMore(false);
                        cancelTar();
                        cancelAsignadas();
                      }
                    }}
                    onFocus={() => {
                      clearTimeout(cliBlur.current);
                      setCliOpen(true);
                    }}
                    onBlur={() => {
                      cliBlur.current = setTimeout(() => setCliOpen(false), 120);
                    }}
                    disabled={overlay.open}
                  />
                  {cliOpen && (
                    <div
                      className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto"
                      onMouseDown={(e) => e.preventDefault()}
                      onScroll={onClientListScroll}
                    >
                      <div className="p-2 border-b bg-gray-50 text-xs text-gray-600">
                        {cliLoading ? "Cargando…" : debCli?.trim() ? "Resultados" : "Explorando"}
                      </div>
                      <div>
                        {cliItems.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            onClick={() => selectCliente(c)}
                          >
                            {c.etiqueta}
                          </button>
                        ))}
                        {!cliLoading && cliItems.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                        )}
                        {cliLoading && (
                          <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tarjeta */}
              <div className="grid grid-cols-[140px_1fr] items-start gap-3 max-w-3xl">
                <label className="label-strong text-right mt-2">Nro. de Tarjeta:</label>
                <div ref={tarBoxRef} className="relative w-full">
                  <input
                    ref={tarInputRef}
                    type="text"
                    placeholder="7002…"
                    className="input w-full"
                    value={tarInput}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTarInput(v);
                      setTarOpen(true);
                      setTarjetaSel(null);

                      const q = (v ?? "").trim();

                      // limpiamos asignaciones cada vez que cambia el número
                      setSelectedEessIds([]);
                      setSelectedProdIds([]);
                      cancelAsignadas();

                      if (clienteSel?.id) {
                        // con cliente → búsquedas / browse por cliente
                        if (!q) {
                          loadTarjetasBrowseCliente(clienteSel.id, 0);
                        } else {
                          loadTarjetasSearchCliente(clienteSel.id, q, 0);
                        }
                      } else {
                        // sin cliente → global
                        if (!q) {
                          loadTarjetasBrowseGlobal(0);
                        } else {
                          loadTarjetasSearchGlobal(q, 0);
                        }
                      }
                    }}
                    onFocus={() => {
                      clearTimeout(tarBlur.current);
                      setTarOpen(true);
                      const q = (tarInput ?? "").trim();

                      if (tarItems.length === 0) {
                        if (clienteSel?.id) {
                          if (q) {
                            loadTarjetasSearchCliente(clienteSel.id, q, 0);
                          } else {
                            loadTarjetasBrowseCliente(clienteSel.id, 0);
                          }
                        } else {
                          if (q) {
                            loadTarjetasSearchGlobal(q, 0);
                          } else {
                            loadTarjetasBrowseGlobal(0);
                          }
                        }
                      }
                    }}
                    onBlur={handleBlurTarjeta}
                    disabled={overlay.open}
                  />
                  {tarOpen && (
                    <div
                      className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto"
                      onMouseDown={(e) => e.preventDefault()}
                      onScroll={onTarListScroll}
                    >
                      <div className="p-2 border-b bg-gray-50 text-xs text-gray-600">
                        {tarLoading ? "Cargando…" : debTar?.trim() ? "Resultados" : "Explorando"}
                      </div>
                      <div>
                        {tarItems.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            onClick={() => selectTarjeta(t)}
                          >
                            {t.etiqueta}
                          </button>
                        ))}
                        {!tarLoading && tarItems.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* === Pestaña PRODUCTOS === */}
          {activeTab === "productos" && (
            <div className="grid grid-cols-[140px_1fr] items-start gap-3 max-w-3xl">
              <label className="label-strong text-right mt-2">Producto:</label>
              <div ref={prodRef} className="relative">
                <button
                  type="button"
                  className="input w-full flex items-center text-left pl-3 pr-2"
                  onClick={() => setOpenProd((v) => !v)}
                  disabled={overlay.open}
                >
                  <span className="truncate">{labelProd}</span>
                  <span className="ml-auto">▾</span>
                </button>
                {openProd && (
                  <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto">
                    <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2">
                      <input
                        placeholder="Filtrar registro"
                        value={searchProd}
                        onChange={(e) => setSearchProd(e.target.value)}
                        className="flex-1 border rounded px-2 py-1"
                      />
                      <span className="text-sm text-gray-600">Opciones:</span>
                      <button
                        type="button"
                        onClick={marcarTodasProd}
                        className="text-green-700 text-sm hover:underline"
                      >
                        ✓ Marcar todas
                      </button>
                      <button
                        type="button"
                        onClick={desmarcarTodasProd}
                        className="text-green-700 text-sm hover:underline"
                      >
                        ✗ Desmarcar todas
                      </button>
                    </div>
                    <div>
                      {filteredProd.map((op) => (
                        <label
                          key={op.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProdIds.includes(op.id)}
                            onChange={() => toggleProd(op.id)}
                          />
                          <span className="truncate">{op.label}</span>
                        </label>
                      ))}
                      {filteredProd.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div>
                      )}
                    </div>
                    <div className="p-2 border-t text-right">
                      <button
                        type="button"
                        onClick={() => setOpenProd(false)}
                        className="text-green-700 hover:text-green-800"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === Pestaña EE.SS. === */}
          {activeTab === "eess" && (
            <div className="grid grid-cols-[140px_1fr] items-center gap-3 max-w-3xl">
              <label className="label-strong text-right">EE.SS.:</label>
              <div ref={eessRef} className="relative">
                <button
                  type="button"
                  className="input w-full flex items-center text-left pl-3 pr-2"
                  onClick={() => setOpenEess((v) => !v)}
                  disabled={overlay.open}
                >
                  <span className="truncate">{labelEess}</span>
                  <span className="ml-auto">▾</span>
                </button>
                {openEess && (
                  <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto">
                    <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2">
                      <input
                        placeholder="Filtrar registro"
                        value={searchEess}
                        onChange={(e) => setSearchEess(e.target.value)}
                        className="flex-1 border rounded px-2 py-1"
                      />
                      <span className="text-sm text-gray-600">Opciones:</span>
                      <button
                        type="button"
                        onClick={marcarTodasEess}
                        className="text-green-700 text-sm hover:underline"
                      >
                        ✓ Marcar todas
                      </button>
                      <button
                        type="button"
                        onClick={desmarcarTodasEess}
                        className="text-green-700 text-sm hover:underline"
                      >
                        ✗ Desmarcar todas
                      </button>
                    </div>

                    <div>
                      {filteredEess.map((op) => (
                        <label
                          key={op.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEessIds.includes(op.id)}
                            onChange={() => toggleEess(op.id)}
                          />
                          <span className="truncate">{op.label}</span>
                        </label>
                      ))}
                      {filteredEess.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div>
                      )}
                    </div>
                    <div className="p-2 border-t text-right">
                      <button
                        type="button"
                        onClick={() => setOpenEess(false)}
                        className="text-green-700 hover:text-green-800"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === Pestaña TODAS LAS TARJETAS === */}
          {activeTab === "todas" && (
            <>
              <div className="grid grid-cols-[140px_1fr] items-center gap-3 max-w-3xl">
                <label className="label-strong text-right">EE.SS.:</label>

                <div ref={eessRef} className="relative">
                  <button
                    type="button"
                    className="input w-full flex items-center text-left pl-3 pr-2"
                    onClick={() => setOpenEess((v) => !v)}
                    disabled={overlay.open}
                  >
                    <span className="truncate">{labelEess}</span>
                    <span className="ml-auto">▾</span>
                  </button>

                  {openEess && (
                    <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto">
                      <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2">
                        <input
                          placeholder="Filtrar registro"
                          value={searchEess}
                          onChange={(e) => setSearchEess(e.target.value)}
                          className="flex-1 border rounded px-2 py-1"
                        />
                        <span className="text-sm text-gray-600">Opciones:</span>
                        <button
                          type="button"
                          onClick={marcarTodasEess}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✓ Marcar todas
                        </button>
                        <button
                          type="button"
                          onClick={desmarcarTodasEess}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✗ Desmarcar todas
                        </button>
                      </div>

                      <div>
                        {filteredEess.map((op) => (
                          <label
                            key={op.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedEessIds.includes(op.id)}
                              onChange={() => toggleEess(op.id)}
                            />
                            <span className="truncate">{op.label}</span>
                          </label>
                        ))}
                        {filteredEess.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div>
                        )}
                      </div>

                      <div className="p-2 border-t text-right">
                        <button
                          type="button"
                          onClick={() => setOpenEess(false)}
                          className="text-green-700 hover:text-green-800"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-[85px_1fr] max-w-3xl mt-3">
                <div></div>
                <div>
                  <div className="p-4 border rounded bg-gray-50">
                    <label className="flex items-center gap-2 text-green-700 font-semibold">
                      <input
                        type="checkbox"
                        checked={confirmarTodas}
                        onChange={(e) => setConfirmarTodas(e.target.checked)}
                        disabled={overlay.open}
                      />
                      Confirmo que deseo ASIGNAR una EE.SS. a TODAS las TARJETAS
                    </label>

                    <div className="text-sm text-gray-600 ml-6">
                      Esta acción afecta a todas las tarjetas activas y en renovación.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t flex items-center gap-2 modal-footer">
          <button
            className={`btn-fil ${
              loading || overlay.open || (activeTab === "todas" && !confirmarTodas)
                ? "opacity-60 cursor-not-allowed"
                : ""
            }`}
            onClick={handleGuardar}
            disabled={loading || overlay.open || (activeTab === "todas" && !confirmarTodas)}
          >
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      {/* ===== Overlay / Popup único ===== */}
      {overlay.open && (
        <div className="fixed inset-0 z-[9999] bg-black/25 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 w-[90%] max-w-md text-center">
            {overlay.mode === "loading" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
                <div className="text-gray-800 font-semibold">Generando solicitud…</div>
                <div className="mt-1 text-sm text-gray-600">
                  Por favor, espere sin cerrar ni recargar la página.
                </div>
              </>
            )}

            {overlay.mode === "success" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-green-600 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-full bg-green-600" />
                </div>
                <div className="text-green-700 font-semibold">Operación realizada</div>
                <div className="mt-1 text-sm text-gray-700">{overlay.message}</div>
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn-fil"
                    onClick={() => setOverlay({ open: false, mode: "hidden", message: "" })}
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}

            {overlay.mode === "error" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-red-600 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-full bg-red-600" />
                </div>
                <div className="text-red-700 font-semibold">Ocurrió un error</div>
                <div className="mt-1 text-sm text-gray-700">{overlay.message}</div>
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn-fil"
                    onClick={() => setOverlay({ open: false, mode: "hidden", message: "" })}
                  >
                    Entendido
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
